/**
 * Application Insights Telemetry Service
 * 
 * Provides client-side telemetry: page views, custom events, errors, and performance.
 * Only initializes when VITE_APPINSIGHTS_CONNECTION_STRING is set (Azure stages).
 */

import { ApplicationInsights } from '@microsoft/applicationinsights-web'

declare const __APP_VERSION__: string

let appInsights: ApplicationInsights | null = null

const connectionString = import.meta.env.VITE_APPINSIGHTS_CONNECTION_STRING || ''
const appEnv = import.meta.env.VITE_APP_ENV || 'local'

export function initTelemetry(): void {
  if (!connectionString || connectionString.startsWith('__')) {
    console.log('[Telemetry] Skipped — no connection string configured')
    return
  }

  // Azure App Insights is Azure-specific telemetry — best-effort only. A bad
  // or unexpected connectionString value must never crash app boot (it did:
  // an uncaught "Please provide instrumentation key" from deep inside the SDK
  // stopped main.ts before app.mount() ran, white-screening the whole app —
  // seen on the Hetzner PoC, which has no App Insights resource at all).
  try {
    appInsights = new ApplicationInsights({
      config: {
        connectionString,
        enableAutoRouteTracking: true,
        enableCorsCorrelation: true,
        correlationHeaderExcludedDomains: [
          '*.kernboards.com',
          '*.posthog.com',
          '*.mews-demo.com',
          '*.mews.com',
          'ca-auth-backend-*',
          'ca-supertokens-*',
          'ca-eventhub-backend-*',
          'ca-eventhub-postgrest-*',
        ],
        enableRequestHeaderTracking: true,
        enableResponseHeaderTracking: true,
        enableAjaxPerfTracking: true,
        disableFetchTracking: false,
        autoTrackPageVisitTime: true,
        enableUnhandledPromiseRejectionTracking: true,
      },
    })

    appInsights.loadAppInsights()

    appInsights.addTelemetryInitializer((envelope) => {
      if (envelope.tags) {
        envelope.tags['ai.cloud.role'] = 'eventhub-frontend'
        envelope.tags['ai.cloud.roleInstance'] = appEnv
      }
    })

    // Register the user-props + route-rewrite initializer eagerly so pageViews
    // emitted before auth resolves (and before any setAuthenticatedUser call)
    // still get the route-name rewrite. The captured `__props` starts empty;
    // setAuthenticatedUser populates it later.
    ensureUserPropsInitializer()

    appInsights.context.application.ver = __APP_VERSION__ ?? 'unknown'

    console.log(`[Telemetry] Application Insights initialized (${appEnv})`)
  } catch (err) {
    appInsights = null
    console.warn('[Telemetry] Application Insights failed to initialize — continuing without it', err)
  }
}

function ensureUserPropsInitializer(): void {
  if (!appInsights || userPropsInitializer) return
  userPropsInitializer = (envelope: any) => {
    const baseData = envelope.baseData || (envelope.baseData = {})
    const properties = baseData.properties || (baseData.properties = {})
    const current = (userPropsInitializer as any).__props || {}
    if (current.email) properties.user_email = current.email
    if (current.role) properties.user_role = current.role
    if (current.property_id != null) properties.user_property_id = String(current.property_id)
    if (current.full_name) properties.user_full_name = current.full_name
    if (currentRouteName) properties.route = currentRouteName
    if (currentRoutePath) properties.route_path = currentRoutePath
    if (envelope.baseType === 'PageviewData' && currentRouteName) {
      baseData.name = currentRouteName
      if (currentRoutePath) baseData.uri = currentRoutePath
    }
  }
  appInsights.addTelemetryInitializer(userPropsInitializer)
}

export function trackEvent(name: string, properties?: Record<string, string>): void {
  appInsights?.trackEvent({ name }, properties)
}

export function trackPageView(name: string, uri?: string): void {
  appInsights?.trackPageView({ name, uri })
}

// Normalize values for AI custom dimensions/properties. AI accepts string
// dictionaries only — booleans/numbers/null become strings; nested objects
// are JSON-stringified so they don't disappear silently.
function normalizeProps(p?: Record<string, any>): Record<string, string> | undefined {
  if (!p) return undefined
  const out: Record<string, string> = {}
  for (const [k, v] of Object.entries(p)) {
    if (v === null || v === undefined) continue
    if (typeof v === 'string') out[k] = v
    else if (typeof v === 'number' || typeof v === 'boolean') out[k] = String(v)
    else {
      try { out[k] = JSON.stringify(v) } catch { /* skip */ }
    }
  }
  return out
}

export function trackException(error: unknown, properties?: Record<string, any>): void {
  if (!appInsights) return
  const exception = error instanceof Error ? error : new Error(String(error))
  appInsights.trackException({ exception }, normalizeProps(properties))
}

export function trackMetric(name: string, average: number, properties?: Record<string, any>): void {
  appInsights?.trackMetric({ name, average }, normalizeProps(properties))
}

// Telemetry initializer that injects user-level custom dimensions onto
// every envelope. We keep a reference so identify can replace the props
// without stacking duplicate initializers on each call.
let userPropsInitializer: ((envelope: any) => void) | null = null

// Captured current route metadata so we can override AI's default
// pageview name (which is just `document.title` — identical on every
// route in our SPA, making dashboards useless). Updated by router.
let currentRouteName: string | null = null
let currentRoutePath: string | null = null

/**
 * Called from the router's afterEach. The next pageView envelope AI emits
 * (driven by enableAutoRouteTracking) gets `name` rewritten to the Vue
 * route name and `url` to the route path so AI dashboards can group by
 * real route instead of `document.title`. Also stamped onto every other
 * envelope as customDimensions.route / route_path for dependency / metric
 * slicing.
 */
export function setCurrentRoute(name: string | null | undefined, path: string | null | undefined): void {
  currentRouteName = name || null
  currentRoutePath = path || null
}

/**
 * Bind the AI session to a user. Sets `ai.user.authUserId` (used to
 * group telemetry by user across machines) AND registers a one-shot
 * initializer that adds role / property_id / email to every envelope's
 * customDimensions. Custom dims survive into the Logs query layer as
 * top-level fields, so dashboards can slice by role just like in PostHog.
 *
 * Pass `undefined` to clear — typically only used by signOut.
 */
export function setAuthenticatedUser(
  userId: string,
  props?: { email?: string; role?: string; property_id?: number | string; full_name?: string }
): void {
  if (!appInsights) return
  appInsights.setAuthenticatedUserContext(userId, undefined, true)

  // Initializer is already registered at initTelemetry time; just update
  // the captured props closure. Only update when actually provided so a
  // per-navigation refresh call (router guard) doesn't wipe role/email.
  ensureUserPropsInitializer()
  if (props && userPropsInitializer) {
    (userPropsInitializer as any).__props = { ...props }
  }
}

export function clearAuthenticatedUser(): void {
  if (!appInsights) return
  appInsights.clearAuthenticatedUserContext()
  // Wipe the captured user-props closure so post-logout envelopes don't
  // carry the previous user's role/email.
  if (userPropsInitializer) {
    (userPropsInitializer as any).__props = {}
  }
}

export function getAppInsights(): ApplicationInsights | null {
  return appInsights
}
